import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useVisitPing } from "@/lib/useVisitPing";
import { useI18n } from "@/lib/i18n";

export default function MarketingLayout({ children }) {
  const { pathname } = useLocation();
  const { lang, setLang, t } = useI18n();
  const onLanding = pathname === "/";
  // Fire one landing-ping per path per tab session so every marketing /
  // SEO route (/, /pets, /blog, /faq, /privacy, /terms, etc.) counts
  // toward "Visitors today" — not just the homepage.
  useVisitPing("landing");

  // Scroll-reveal — observes every `[data-reveal]` descendant and
  // toggles `.is-revealed` once they intersect. Kept in the marketing
  // shell so any section in any marketing page picks it up for free.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) {
      // No-op fallback: just reveal everything immediately so users on
      // older browsers don't see invisible content.
      document.querySelectorAll("[data-reveal]").forEach((el) => {
        el.classList.add("is-revealed");
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-revealed");
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    document.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);

  // Sticky pill nav — once the reader scrolls past the hero
  // (~120px), the header transforms into a floating rounded pill.
  // Uses `.is-pinned` toggled on the <header> so the CSS transition
  // owns all the motion. rAF-throttled to keep it silky on iPad.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const header = document.querySelector(".marketing-v2 header");
    if (!header) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const scrolled = window.scrollY > 120;
        header.classList.toggle("is-pinned", scrolled);
        raf = null;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname]);

  return (
    <div
      className="marketing-v2 min-h-screen bg-[color:var(--bg)] text-[color:var(--ink)] flex flex-col"
      data-testid="marketing-shell"
    >
      <header className="px-6 md:px-10 py-6 flex items-center justify-between">
        <Link
          to="/"
          className="label-tiny tracking-widest"
          data-testid="nav-brand"
        >
          FamFeel
        </Link>
        <nav className="flex items-center gap-2 md:gap-3">
          {/* Language toggle — flag-pill. Cycles between EN and DA.
              Explicit choice takes precedence over the auto-detected
              default (which itself defaults to Danish for da-* browsers). */}
          <button
            type="button"
            onClick={() => setLang(lang === "da" ? "en" : "da")}
            className="ff-btn text-[13px] px-3"
            data-testid="nav-lang-toggle"
            aria-label={lang === "da" ? "Switch to English" : "Skift til dansk"}
            title={lang === "da" ? "Switch to English" : "Skift til dansk"}
          >
            <span className="mr-1.5" aria-hidden="true">
              {lang === "da" ? "🇬🇧" : "🇩🇰"}
            </span>
            <span className="tracking-wide">
              {lang === "da" ? "EN" : "DA"}
            </span>
          </button>
          {!onLanding && (
            <Link to="/" className="ff-btn" data-testid="nav-home">
              {t({ en: "Home", da: "Forside" })}
            </Link>
          )}
          <Link to="/app" className="ff-btn ff-btn-primary" data-testid="nav-open-app">
            {t({ en: "Open app →", da: "Åbn app →" })}
          </Link>
        </nav>
      </header>
      {/* Reserves height when the header pins into a floating pill
          so the page below doesn't jump up when it detaches from
          the flow. Height comes back to 0 when the header goes
          inline again (via CSS). */}
      <div className="header-spacer" aria-hidden="true" />
      <main className="flex-1">{children}</main>
      <footer className="px-6 md:px-10 py-8 mt-20">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-x-6 gap-y-2 items-center justify-between">
          <div className="label-tiny text-[color:var(--ink-3)]">
            {t({ en: "FamFeel · Private by default", da: "FamFeel" })}
          </div>
          <nav className="flex flex-wrap gap-4 label-tiny">
            <Link
              to="/blog"
              className="hover:text-[color:var(--ink)] transition-colors"
              data-testid="footer-blog"
            >
              {t({ en: "Blog", da: "Blog" })}
            </Link>
            <Link
              to="/faq"
              className="hover:text-[color:var(--ink)] transition-colors"
              data-testid="footer-faq"
            >
              {t({ en: "FAQ", da: "FAQ" })}
            </Link>
            <Link
              to="/reviews"
              className="hover:text-[color:var(--ink)] transition-colors"
              data-testid="footer-reviews"
            >
              {t({ en: "Reviews", da: "Anmeldelser" })}
            </Link>
            <Link
              to="/privacy"
              className="hover:text-[color:var(--ink)] transition-colors"
              data-testid="footer-privacy"
            >
              {t({ en: "Privacy", da: "Privatliv" })}
            </Link>
            <Link
              to="/terms"
              className="hover:text-[color:var(--ink)] transition-colors"
              data-testid="footer-terms"
            >
              {t({ en: "Terms", da: "Vilkår" })}
            </Link>
            <Link
              to="/admin"
              className="text-[10px] text-[color:var(--ink-3)]/50 hover:text-[color:var(--ink-3)] transition-colors ml-2"
              data-testid="footer-admin"
              title="Admin"
            >
              ·
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
